import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import Stack from '@mui/material/Stack';

import useCooperativeId from 'shared/hooks/useCooperativeId';

import { fileLoaderApi } from '../../api';
import { shouldSkipOptionsQuery } from '../../api/helpers';
import { type Document, EntityType } from '../../model';
import Dropzone, { type DropzoneProps } from './Dropzone';
import { ServerFilesList } from '../FilesList';

type FileLoaderProps = {
  cached?: boolean;
  entityType: EntityType;
  entityId: string;
  /**
   * If `true` - fetching documents will be omited
   *
   * @default true
   */
  hasDocuments?: boolean;
  readonly?: boolean;
} & Pick<DropzoneProps, 'validateFns'>;

const emptyDocumentsList: Document[] = [];

const FileLoader = ({
  cached,
  entityId,
  entityType,
  hasDocuments = true,
  readonly = false,
  validateFns,
}: FileLoaderProps) => {
  const cooperativeId = useCooperativeId();

  const baseRequestParams = useMemo(
    () =>
      cooperativeId
        ? {
            cooperativeId,
            entityId,
            entityType,
          }
        : null,
    [cooperativeId, entityId, entityType]
  );

  const getDocumentsOptionsQueryState =
    fileLoaderApi.useGetDocumentsOptionsQuery(baseRequestParams ?? skipToken, {
      skip: shouldSkipOptionsQuery({ readonly, entityType }),
      refetchOnMountOrArgChange: true,
    });

  const getDocumentsQueryState = fileLoaderApi.useGetAttachmentsQuery(
    cooperativeId
      ? {
          cooperativeId,
          entityId,
          entityType,
          cached,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
      skip: !hasDocuments,
      selectFromResult: ({ isError, isFetching, isLoading, data }) => ({
        documents: data ?? emptyDocumentsList,
        isError,
        isFetching,
        isLoading,
      }),
    }
  );
  const [uploadDocuments, uploadDocumentsMutationState] =
    fileLoaderApi.useUploadDocumentMutation();

  const handleUploadDocuments = (files: File[]) => {
    if (!baseRequestParams) return;

    const formData = new FormData();

    for (const file of files) {
      formData.append('file', file, file.name);
    }

    uploadDocuments({
      ...baseRequestParams,
      formData,
    });
  };

  const showDropzone = useMemo(() => {
    if (readonly) return false;

    return getDocumentsOptionsQueryState.isSuccess
      ? getDocumentsOptionsQueryState.data?.canCreateDocuments
      : true;
  }, [readonly, getDocumentsOptionsQueryState]);

  return (
    <Stack spacing={2}>
      <ServerFilesList
        documents={getDocumentsQueryState.documents}
        entityId={entityId}
        entityType={entityType}
        isLoading={
          getDocumentsQueryState.isLoading ||
          getDocumentsQueryState.isFetching ||
          getDocumentsOptionsQueryState.isLoading ||
          getDocumentsOptionsQueryState.isFetching
        }
        readonly={readonly}
      />
      {showDropzone && (
        <Dropzone
          isLoading={uploadDocumentsMutationState.isLoading}
          validateFns={validateFns}
          onChange={handleUploadDocuments}
        />
      )}
    </Stack>
  );
};

export default FileLoader;
